import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import BalanceMonitor from '../components/BalanceMonitor';
import WithdrawalManager from '../components/WithdrawalManager';
import NotificationBell from '../components/NotificationBell';
import SearchAndSort from '../components/SearchAndSort';
import AloriaLogo from '../components/AloriaLogo';
import ProfileSettings from '../components/ProfileSettings';
import useSocket from '../hooks/useSocket';
import { Button } from '../components/ui/button';
import { Settings, LogOut, TrendingUp, TrendingDown, Clock, CheckCircle, FileText, Plus, Download, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiErrorMessage } from '../utils/errors';

export default function AccountantDashboard() {
  const { user, logout } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('payments');
  const [stats, setStats] = useState({
    pending_payments: 0,
    confirmed_payments: 0,
    total_payments: 0,
    total_withdrawals: 0,
    current_balance: 0
  });
  const [loading, setLoading] = useState(true);
  const [showProfileSettings, setShowProfileSettings] = useState(false);

  // Payments state
  const [pendingPayments, setPendingPayments] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [filteredPendingPayments, setFilteredPendingPayments] = useState([]);
  const [filteredPaymentHistory, setFilteredPaymentHistory] = useState([]);
  const [rejectionDialog, setRejectionDialog] = useState({ show: false, payment: null, reason: '' });
  const [confirmationDialog, setConfirmationDialog] = useState({ show: false, payment: null, code: '', generatedCode: '' });

  // Manual invoice state
  const [showManualInvoiceDialog, setShowManualInvoiceDialog] = useState(false);
  const [showManualInvoiceConfirmation, setShowManualInvoiceConfirmation] = useState(false);
  const [manualInvoice, setManualInvoice] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    amount: '',
    description: '',
    payment_method: 'Cash'
  });
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  // WebSocket pour mise à jour en temps réel
  useSocket(localStorage.getItem('token'));

  useEffect(() => {
    fetchAllData();
  }, []);

  // Écouter les événements de paiement pour rafraîchir automatiquement
  useEffect(() => {
    const handlePaymentDeclared = () => {
      fetchPayments();
    };
    const handlePaymentUpdated = () => {
      fetchPayments();
      fetchStats();
    };
    const handleNewNotification = () => {
      fetchPayments();
    };

    window.addEventListener('paymentDeclared', handlePaymentDeclared);
    window.addEventListener('paymentUpdated', handlePaymentUpdated);
    window.addEventListener('newNotification', handleNewNotification);

    return () => {
      window.removeEventListener('paymentDeclared', handlePaymentDeclared);
      window.removeEventListener('paymentUpdated', handlePaymentUpdated);
      window.removeEventListener('newNotification', handleNewNotification);
    };
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchStats(), fetchPayments()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const balanceRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/balance/current`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const balance = balanceRes.ok ? await balanceRes.json() : {};
      setStats(prev => ({
        ...prev,
        total_payments: balance.total_payments || 0,
        total_withdrawals: balance.total_withdrawals || 0,
        current_balance: balance.current_balance || 0
      }));
    } catch (e) {
      console.error('Error fetching stats:', e);
    }
  };

  const fetchPayments = async () => {
    try {
      const [pendingRes, historyRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/pending`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/history`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      const pending = pendingRes.ok ? await pendingRes.json() : [];
      const history = historyRes.ok ? await historyRes.json() : [];
      if (!pendingRes.ok) {
        console.error('Erreur chargement paiements en attente:', pendingRes.status);
        toast.error('Erreur lors du chargement des paiements en attente');
      }
      if (!historyRes.ok) {
        console.error('Erreur chargement historique:', historyRes.status);
        toast.error("Erreur lors du chargement de l'historique des paiements");
      }
      setPendingPayments(pending);
      setPaymentHistory(history);
      const confirmed = history.filter(p => p.status && p.status.toUpperCase() === 'CONFIRMED');
      setStats(prev => ({
        ...prev,
        pending_payments: pending.length,
        confirmed_payments: confirmed.length
      }));
    } catch (e) {
      console.error('Error fetching payments:', e);
    }
  };

  const refreshData = () => {
    fetchStats();
    fetchPayments();
  };

  // ─── Payment Confirmation Flow (2-step with code) ───
  const handlePaymentAction = async (payment, action) => {
    if (action === 'REJECTED') {
      setRejectionDialog({ show: true, payment, reason: '' });
      return;
    }
    if (action === 'CONFIRMED') {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/${payment.id}/confirm`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ action: 'CONFIRMED' })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.detail || result.message || 'Échec de génération du code');
        setConfirmationDialog({
          show: true,
          payment,
          code: '',
          generatedCode: result.confirmation_code || ''
        });
      } catch (error) {
        toast.error(error.message || 'Erreur lors de la confirmation');
      }
    }
  };

  const handleConfirmWithCode = async () => {
    const { payment, code } = confirmationDialog;
    if (!code || code.trim() === '') {
      toast.error('Veuillez saisir le code de confirmation');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/${payment.id}/confirm`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ action: 'CONFIRMED', confirmation_code: code.trim() })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(formatApiErrorMessage(errorData, 'Code de confirmation incorrect'));
      }
      toast.success('✅ Paiement confirmé avec succès!');
      setConfirmationDialog({ show: false, payment: null, code: '', generatedCode: '' });
      refreshData();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la confirmation');
    }
  };

  const handleRejection = async () => {
    if (!rejectionDialog.reason.trim()) {
      toast.error('Veuillez fournir une raison de rejet');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/${rejectionDialog.payment.id}/confirm`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ action: 'REJECTED', rejection_reason: rejectionDialog.reason })
      });
      if (!response.ok) throw new Error('Failed to reject payment');
      toast.success('Paiement rejeté');
      setRejectionDialog({ show: false, payment: null, reason: '' });
      refreshData();
    } catch (error) {
      toast.error('Erreur lors du rejet du paiement');
    }
  };

  const downloadInvoice = async (paymentId, invoiceNumber) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/${paymentId}/invoice`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Facture_${invoiceNumber || paymentId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Facture téléchargée!');
      } else {
        toast.error('Impossible de télécharger la facture');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  // ─── Manual Invoice ───
  const handleGenerateManualInvoice = async () => {
    if (!manualInvoice.client_name.trim() || !manualInvoice.amount || parseFloat(manualInvoice.amount) <= 0) {
      toast.error('Veuillez remplir tous les champs obligatoires (nom du client et montant)');
      return;
    }
    setShowManualInvoiceConfirmation(true);
  };

  const confirmGenerateManualInvoice = async () => {
    setShowManualInvoiceConfirmation(false);
    setGeneratingInvoice(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/manual-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          client_name: manualInvoice.client_name,
          client_email: manualInvoice.client_email || null,
          client_phone: manualInvoice.client_phone || null,
          amount: parseFloat(manualInvoice.amount),
          description: manualInvoice.description || 'Facture manuelle - Services d\'immigration',
          payment_method: manualInvoice.payment_method,
          currency: 'CFA'
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(formatApiErrorMessage(errorData, 'Erreur lors de la génération de la facture'));
      }
      const invoiceNumber = response.headers.get('X-Invoice-Number');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Facture_${invoiceNumber || 'manuelle'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('✅ Facture manuelle générée avec succès !');
      setManualInvoice({ client_name: '', client_email: '', client_phone: '', amount: '', description: '', payment_method: 'Cash' });
      setShowManualInvoiceDialog(false);
      refreshData();
    } catch (error) {
      console.error('Erreur génération facture manuelle:', error);
      toast.error(error.message || 'Erreur lors de la génération de la facture');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <Card className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] border-none shadow-2xl rounded-none">
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <AloriaLogo className="h-10" />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-white">Comptabilité</h1>
                <p className="text-sm text-slate-400 mt-1">Bienvenue, {user?.full_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowProfileSettings(!showProfileSettings)}
                className="bg-slate-700 hover:bg-slate-600 text-white"
                title="Mon Profil"
              >
                <Settings className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Profil</span>
              </Button>
              <NotificationBell />
              <Button onClick={logout} className="bg-slate-700 hover:bg-slate-600 text-white">
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Settings */}
      {showProfileSettings && (
        <div className="p-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Mon Profil</h2>
              <Button onClick={() => setShowProfileSettings(false)} variant="outline" className="border-slate-600 text-white">Retour</Button>
            </div>
            <ProfileSettings user={user} onUpdate={refreshData} />
          </div>
        </div>
      )}

      {!showProfileSettings && (
      <div className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-l-4 border-l-emerald-500 border-slate-700">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-emerald-500/10">
                <Wallet className="h-7 w-7 text-emerald-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.current_balance.toLocaleString()} <span className="text-sm font-normal text-slate-400">CFA</span></div>
                <div className="text-slate-400 text-sm">Solde Actuel</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-l-4 border-l-blue-500 border-slate-700">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <TrendingUp className="h-7 w-7 text-blue-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.total_payments.toLocaleString()} <span className="text-sm font-normal text-slate-400">CFA</span></div>
                <div className="text-slate-400 text-sm">Total Entrées</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-l-4 border-l-orange-500 border-slate-700">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-orange-500/10">
                <TrendingDown className="h-7 w-7 text-orange-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.total_withdrawals.toLocaleString()} <span className="text-sm font-normal text-slate-400">CFA</span></div>
                <div className="text-slate-400 text-sm">Total Sorties</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-l-4 border-l-yellow-500 border-slate-700">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-yellow-500/10">
                <Clock className="h-7 w-7 text-yellow-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.pending_payments}</div>
                <div className="text-slate-400 text-sm">En Attente</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-l-4 border-l-green-500 border-slate-700">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-green-500/10">
                <CheckCircle className="h-7 w-7 text-green-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.confirmed_payments}</div>
                <div className="text-slate-400 text-sm">Confirmés</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="-mx-4 sm:mx-0">
            <TabsList className="flex w-full overflow-x-auto md:grid md:grid-cols-3 bg-[#1E293B] border border-slate-700 p-1.5 sm:p-2 gap-1 sm:gap-2 scrollbar-hide">
              <TabsTrigger value="payments" className="flex-shrink-0 min-w-[120px] md:min-w-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white text-slate-300 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-lg transition-all">💳 Paiements</TabsTrigger>
              <TabsTrigger value="withdrawals" className="flex-shrink-0 min-w-[120px] md:min-w-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white text-slate-300 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-lg transition-all">💸 Retraits</TabsTrigger>
              <TabsTrigger value="financial" className="flex-shrink-0 min-w-[120px] md:min-w-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white text-slate-300 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap rounded-lg transition-all">📊 Financier</TabsTrigger>
            </TabsList>
          </div>

          {/* ═══ PAIEMENTS TAB ═══ */}
          <TabsContent value="payments">
            <div className="space-y-6">
              {/* Manual Invoice Button */}
              <Card className="bg-gradient-to-r from-orange-500/10 to-orange-600/10 border-orange-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-orange-400" />
                        Générer une Facture Manuelle
                      </h3>
                      <p className="text-slate-400 text-sm mt-1">Créez une facture professionnelle pour tout paiement reçu</p>
                    </div>
                    <Button onClick={() => setShowManualInvoiceDialog(true)} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white">
                      <Plus className="w-4 h-4 mr-2" />
                      Nouvelle Facture
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Pending Payments */}
                <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center space-x-2">
                      <span className="text-orange-500">💰</span>
                      <span>Paiements En Attente</span>
                      {pendingPayments.length > 0 && (
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 ml-2">{pendingPayments.length}</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="text-slate-400">Validez ou rejetez les déclarations de paiement des clients</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {pendingPayments.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-4xl mb-3">✅</div>
                        <p className="text-slate-400">Aucun paiement en attente</p>
                        <p className="text-slate-500 text-sm">Tous les paiements ont été traités</p>
                      </div>
                    ) : (
                      <>
                        <SearchAndSort
                          data={pendingPayments}
                          searchFields={['client_name', 'payment_method', 'description']}
                          sortOptions={[
                            { value: 'declared_at', label: 'Date de déclaration' },
                            { value: 'client_name', label: 'Nom du client' },
                            { value: 'amount', label: 'Montant' }
                          ]}
                          onFilteredDataChange={setFilteredPendingPayments}
                          placeholder="Rechercher un paiement..."
                        />
                        <div className="space-y-4 max-h-96 overflow-y-auto mt-4">
                          {filteredPendingPayments.map((payment) => (
                            <div key={payment.id} className="bg-slate-600 rounded-lg p-4 border border-slate-500 space-y-3">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="text-white font-semibold text-lg">{payment.amount} {payment.currency}</p>
                                  <p className="text-slate-300 text-sm">{payment.client_name}</p>
                                </div>
                                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">⏳ En attente</Badge>
                              </div>
                              <div className="space-y-1 text-sm">
                                <p className="text-slate-400"><span className="font-medium text-slate-300">Méthode:</span> {payment.payment_method}</p>
                                {payment.reference && <p className="text-slate-400"><span className="font-medium text-slate-300">Référence:</span> {payment.reference}</p>}
                                {payment.description && <p className="text-slate-400"><span className="font-medium text-slate-300">Description:</span> {payment.description}</p>}
                                <p className="text-slate-400"><span className="font-medium text-slate-300">Déclaré le:</span> {new Date(payment.declared_at).toLocaleDateString('fr-FR')}</p>
                              </div>
                              <div className="flex gap-2">
                                <Button onClick={() => handlePaymentAction(payment, 'CONFIRMED')} className="flex-1 bg-green-600 hover:bg-green-700 text-white" size="sm">✅ Confirmer</Button>
                                <Button onClick={() => handlePaymentAction(payment, 'REJECTED')} className="flex-1 bg-red-600 hover:bg-red-700 text-white" size="sm">❌ Rejeter</Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Payment History */}
                <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center space-x-2">
                      <span className="text-green-500">📋</span>
                      <span>Historique des Paiements</span>
                    </CardTitle>
                    <CardDescription className="text-slate-400">Paiements traités</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {paymentHistory.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-4xl mb-3">📋</div>
                        <p className="text-slate-400">Aucun historique</p>
                        <p className="text-slate-500 text-sm">Les paiements traités apparaîtront ici</p>
                      </div>
                    ) : (
                      <>
                        <SearchAndSort
                          data={paymentHistory}
                          searchFields={['client_name', 'payment_method', 'invoice_number', 'description']}
                          sortOptions={[
                            { value: 'confirmed_at', label: 'Date de traitement' },
                            { value: 'declared_at', label: 'Date de déclaration' },
                            { value: 'client_name', label: 'Client' },
                            { value: 'amount', label: 'Montant' },
                            { value: 'status', label: 'Statut' }
                          ]}
                          onFilteredDataChange={setFilteredPaymentHistory}
                          placeholder="Rechercher dans l'historique..."
                        />
                        <div className="space-y-3 max-h-96 overflow-y-auto mt-4">
                          {filteredPaymentHistory.map((payment) => (
                            <div key={payment.id} className="bg-slate-600 rounded-lg p-3 border border-slate-500">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <p className="text-white font-medium">{payment.amount} {payment.currency}</p>
                                  <p className="text-slate-300 text-sm">{payment.client_name}</p>
                                </div>
                                <Badge className={
                                  payment.status === 'confirmed' || payment.status === 'CONFIRMED'
                                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                    : payment.status === 'rejected' || payment.status === 'REJECTED'
                                    ? 'bg-red-500/20 text-red-400 border-red-500/30'
                                    : 'bg-orange-500/20 text-orange-400 border-orange-500/30'
                                }>
                                  {payment.status === 'confirmed' || payment.status === 'CONFIRMED' ? '✅ Confirmé'
                                    : payment.status === 'rejected' || payment.status === 'REJECTED' ? '❌ Rejeté'
                                    : '⏳ En attente'}
                                </Badge>
                              </div>
                              <p className="text-xs text-slate-400">{new Date(payment.confirmed_at || payment.declared_at).toLocaleString('fr-FR')}</p>
                              {payment.invoice_number && (
                                <p className="text-xs text-slate-400 mt-1"><span className="font-medium">Facture:</span> {payment.invoice_number}</p>
                              )}
                              {(payment.status === 'CONFIRMED' || payment.status === 'confirmed') && payment.invoice_number && (
                                <button
                                  onClick={() => downloadInvoice(payment.id, payment.invoice_number)}
                                  className="mt-2 w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-2 py-1 rounded text-xs transition-colors"
                                >
                                  <Download className="w-3 h-3" />
                                  Télécharger facture
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* ═══ RETRAITS TAB ═══ */}
          <TabsContent value="withdrawals">
            <WithdrawalManager />
          </TabsContent>

          {/* ═══ FINANCIER TAB ═══ */}
          <TabsContent value="financial">
            <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Solde & Flux Financiers</CardTitle>
                <CardDescription className="text-slate-400">Consultez le solde global et les statistiques financières détaillées.</CardDescription>
              </CardHeader>
              <CardContent>
                <BalanceMonitor />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      )}

      {/* ═══ DIALOGS ═══ */}

      {/* Rejection Dialog */}
      <Dialog open={rejectionDialog.show} onOpenChange={(open) => setRejectionDialog({ ...rejectionDialog, show: open })}>
        <DialogContent className="bg-[#1E293B] border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Rejeter le Paiement</DialogTitle>
            <DialogDescription className="text-slate-400">Veuillez indiquer la raison du rejet</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Raison du rejet *</Label>
              <Input
                value={rejectionDialog.reason}
                onChange={(e) => setRejectionDialog({ ...rejectionDialog, reason: e.target.value })}
                placeholder="Ex: Montant incorrect, preuve de paiement invalide..."
                className="bg-slate-800 border-slate-600 text-white mt-2"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleRejection} className="flex-1 bg-red-600 hover:bg-red-700 text-white">Confirmer le Rejet</Button>
              <Button onClick={() => setRejectionDialog({ show: false, payment: null, reason: '' })} variant="outline" className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Code Dialog */}
      <Dialog open={confirmationDialog.show} onOpenChange={(open) => setConfirmationDialog({ ...confirmationDialog, show: open })}>
        <DialogContent className="bg-[#1E293B] border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Confirmer le Paiement</DialogTitle>
            <DialogDescription className="text-slate-400">Saisissez le code de confirmation pour valider le paiement</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-lg">
              <p className="text-blue-400 text-sm font-medium">
                Code généré: <span className="font-mono text-lg">{confirmationDialog.generatedCode}</span>
              </p>
              <p className="text-blue-300 text-xs mt-1">Veuillez saisir ce code ci-dessous pour confirmer</p>
            </div>
            <div>
              <Label className="text-slate-300">Code de confirmation *</Label>
              <Input
                value={confirmationDialog.code}
                onChange={(e) => setConfirmationDialog({ ...confirmationDialog, code: e.target.value })}
                placeholder="Saisissez le code de confirmation"
                className="bg-slate-800 border-slate-600 text-white mt-2"
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleConfirmWithCode} className="flex-1 bg-green-600 hover:bg-green-700 text-white" disabled={!confirmationDialog.code?.trim()}>✅ Confirmer le Paiement</Button>
              <Button onClick={() => setConfirmationDialog({ show: false, payment: null, code: '', generatedCode: '' })} variant="outline" className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Invoice Dialog */}
      <Dialog open={showManualInvoiceDialog} onOpenChange={setShowManualInvoiceDialog}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-lg sm:text-xl">📄 Générer une Facture Manuelle</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Créez une facture professionnelle pour un paiement reçu. Le montant sera automatiquement ajouté au solde.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-slate-300 text-sm">Nom du Client *</Label>
                <Input value={manualInvoice.client_name} onChange={(e) => setManualInvoice({...manualInvoice, client_name: e.target.value})} placeholder="Ex: Jean Dupont" className="bg-slate-800 border-slate-600 text-white mt-1.5 h-10" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Email du Client</Label>
                <Input type="email" value={manualInvoice.client_email} onChange={(e) => setManualInvoice({...manualInvoice, client_email: e.target.value})} placeholder="client@example.com" className="bg-slate-800 border-slate-600 text-white mt-1.5 h-10" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-slate-300 text-sm">Téléphone du Client</Label>
                <Input value={manualInvoice.client_phone} onChange={(e) => setManualInvoice({...manualInvoice, client_phone: e.target.value})} placeholder="+237 6XX XXX XXX" className="bg-slate-800 border-slate-600 text-white mt-1.5 h-10" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Montant (CFA) *</Label>
                <Input type="number" value={manualInvoice.amount} onChange={(e) => setManualInvoice({...manualInvoice, amount: e.target.value})} placeholder="Ex: 50000" className="bg-slate-800 border-slate-600 text-white mt-1.5 h-10" min="0" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Description</Label>
              <Input value={manualInvoice.description} onChange={(e) => setManualInvoice({...manualInvoice, description: e.target.value})} placeholder="Services d'immigration et conseil" className="bg-slate-800 border-slate-600 text-white mt-1.5 h-10" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Méthode de Paiement *</Label>
              <Select value={manualInvoice.payment_method} onValueChange={(value) => setManualInvoice({...manualInvoice, payment_method: value})}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white mt-1.5 h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="Cash" className="text-white">💵 Cash</SelectItem>
                  <SelectItem value="Mobile Money" className="text-white">📱 Mobile Money</SelectItem>
                  <SelectItem value="Virement bancaire" className="text-white">🏦 Virement bancaire</SelectItem>
                  <SelectItem value="Carte bancaire" className="text-white">💳 Carte bancaire</SelectItem>
                  <SelectItem value="Autre" className="text-white">➕ Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 sm:p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-slate-300 font-medium text-sm sm:text-base">Montant total:</span>
                <span className="text-white text-xl sm:text-2xl font-bold">{manualInvoice.amount ? parseFloat(manualInvoice.amount).toLocaleString() : '0'} CFA</span>
              </div>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-blue-300 text-xs sm:text-sm flex items-start gap-2">
                <span className="flex-shrink-0">ℹ️</span>
                <span>La facture sera générée au format PDF professionnel et le montant sera automatiquement ajouté au solde de l'entreprise.</span>
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowManualInvoiceDialog(false)} className="w-full sm:flex-1 border-slate-600 text-white hover:bg-slate-700 h-10" disabled={generatingInvoice}>Annuler</Button>
            <Button onClick={handleGenerateManualInvoice} className="w-full sm:flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white h-10" disabled={generatingInvoice}>
              {generatingInvoice ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div><span className="text-sm sm:text-base">Génération...</span></>) : (<><FileText className="w-4 h-4 mr-2" /><span className="text-sm sm:text-base">Générer la Facture</span></>)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Invoice Confirmation Dialog */}
      <Dialog open={showManualInvoiceConfirmation} onOpenChange={setShowManualInvoiceConfirmation}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              <span className="text-2xl">⚠️</span>
              Confirmation de Génération
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
              <p className="text-orange-300 text-sm leading-relaxed">Vous êtes sur le point de générer une facture manuelle pour :</p>
              <div className="mt-3 space-y-2 text-white">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Client:</span>
                  <span className="font-semibold">{manualInvoice.client_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Montant:</span>
                  <span className="font-bold text-lg text-orange-400">{manualInvoice.amount ? parseFloat(manualInvoice.amount).toLocaleString() : '0'} CFA</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Paiement:</span>
                  <span className="font-medium">{manualInvoice.payment_method}</span>
                </div>
              </div>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <p className="text-blue-300 text-sm leading-relaxed"><span className="font-semibold">Important:</span> Cette action va :</p>
              <ul className="mt-2 space-y-1.5 text-blue-300 text-sm">
                <li className="flex items-start gap-2"><span className="text-blue-400 flex-shrink-0">•</span><span>Générer une facture PDF professionnelle</span></li>
                <li className="flex items-start gap-2"><span className="text-blue-400 flex-shrink-0">•</span><span>Ajouter <strong>{manualInvoice.amount ? parseFloat(manualInvoice.amount).toLocaleString() : '0'} CFA</strong> au solde de l'entreprise</span></li>
                <li className="flex items-start gap-2"><span className="text-blue-400 flex-shrink-0">•</span><span>Créer un enregistrement permanent dans l'historique</span></li>
              </ul>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-300 text-xs flex items-start gap-2">
                <span className="flex-shrink-0">⚠️</span>
                <span>Assurez-vous d'avoir bien reçu le paiement avant de valider. Cette action ne peut pas être annulée.</span>
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowManualInvoiceConfirmation(false)} className="w-full sm:flex-1 border-slate-600 text-white hover:bg-slate-700" disabled={generatingInvoice}>Annuler</Button>
            <Button onClick={confirmGenerateManualInvoice} className="w-full sm:flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white" disabled={generatingInvoice}>
              {generatingInvoice ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Génération...</>) : (<>✓ Confirmer et Générer</>)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
